import React, { useEffect, useState } from 'react';
import { Button, Dialog, List, Popup, Steps, Toast } from 'antd-mobile';
import dayjs from 'dayjs';
import styles from './index.module.scss';
import {
  getDelayEnergyConsumptionDetail,
  GetDelayEnergyConsumptionDetailPostResponse,
  deleteDelayEnergyConsumption,
  revokeApproval,
  resubmitApproval,
  ApprovalDetail,
  encourageCompletion,
} from '@/api/energy';
import { isEmpty, isNil } from 'lodash-es';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApprovalStatus, ApprovalStatusColorMap, ApprovalStatusMap, StepStatusMap } from './util';
import { ApprovalStatus as OutApprovalStatus } from '../EnergyUseApplication/util';
import Approval from './components/Approval';
import actualApprovalImg from './imgs/actualApproval@2x.png';
import unActualApprovalImg from './imgs/unActualApproval@2x.png';
const { Step } = Steps;

// 按钮优先级配置：数字越小优先级越高
const ACTION_PRIORITY: Record<string, number> = {
  approval: 1, // 审批
  encourage: 2, // 催办
  revoke: 3, // 撤回
  delete: 4, // 删除
  edit: 10, // 编辑
  resubmit: 11, // 重新发起
  approvalList: 12, // 审批列表
};

const EnergyUseApplicationDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const status = Number(searchParams.get('status')) as unknown as OutApprovalStatus;
  const [detail, setDetail] = useState<GetDelayEnergyConsumptionDetailPostResponse>({});
  const [showApprovalPopup, setShowApprovalPopup] = useState<boolean>(false);
  const [showMorePopup, setShowMorePopup] = useState<boolean>(false);
  const [level, setLevel] = useState<number>();

  interface ActionItem {
    key: string;
    node: React.ReactNode;
  }
  const actionList: ActionItem[] = [];

  useEffect(() => {
    document.title = '查看详情';
  }, []);

  useEffect(() => {
    if (id) {
      getDelayEnergyConsumptionDetail(id).then(res => {
        if (res) {
          setDetail(res);
          setLevel(res.level);
        }
      });
    }
  }, [id]);

  const DescRender = (approvalData: ApprovalDetail) => (
    <>
      <div className={styles.stepDescItem}>
        审批人：
        {(approvalData?.staffList ?? []).map((staff, index) => {
          return (
            <>
              <img src={staff.isActualApproval ? actualApprovalImg : unActualApprovalImg} alt="审批人" width={12} />
              {staff.staffName}
              {approvalData!.staffList!.length > index + 1 ? '、' : ''}
            </>
          );
        })}
      </div>
      <div className={styles.stepDescItem}>
        审批时间：{approvalData?.approvalTime ? dayjs(approvalData?.approvalTime).format('YYYY-MM-DD HH:mm:ss') : '-'}
      </div>
      <div className={styles.stepDescItem}>
        审批结果：
        {isNil(approvalData?.approvalResult) || approvalData?.approvalResult === ApprovalStatus.NOT_ARRIVED ? (
          '-'
        ) : (
          <span style={{ color: ApprovalStatusColorMap[approvalData?.approvalResult as ApprovalStatus] }}>
            {ApprovalStatusMap[approvalData?.approvalResult as ApprovalStatus]}
          </span>
        )}
      </div>
      <div className={styles.stepDescItem}>备注：{approvalData?.remark || '无'}</div>
    </>
  );

  // 催办功能
  const handleEncourage = () => {
    encourageCompletion(detail.id!).then(() => {
      Toast.show({
        icon: 'success',
        content: '催办成功',
      });
    });
  };

  // 收集所有操作按钮，按优先级排序后展示
  if (detail.approvable) {
    actionList.push({
      key: 'approval',
      node: (
        <Button color="primary" size="large" onClick={() => setShowApprovalPopup(true)}>
          审批
        </Button>
      ),
    });
  }
  if (detail.admin && (status === OutApprovalStatus.PENDING_FIRST || status === OutApprovalStatus.PENDING_SECOND)) {
    actionList.push({
      key: 'encourage',
      node: (
        <Button onClick={handleEncourage} size="large">
          催办
        </Button>
      ),
    });
  }
  if (detail.admin && status === OutApprovalStatus.PENDING_FIRST) {
    actionList.push({
      key: 'revoke',
      node: (
        <Button
          color="danger"
          size="large"
          onClick={() => {
            Dialog.confirm({
              content: '确定撤回延时申请？',
              confirmText: '确定',
              onConfirm: () => {
                revokeApproval(detail.id!).then(() => navigate('/intelligent-platform/energyUse/application'));
              },
            });
          }}
        >
          撤回
        </Button>
      ),
    });
  }
  if (detail.admin) {
    actionList.push({
      key: 'delete',
      node: (
        <Button
          size="large"
          onClick={() => {
            Dialog.confirm({
              content: '确定要删除？',
              confirmText: '确定',
              onConfirm: () => {
                deleteDelayEnergyConsumption(detail.id!).then(() =>
                  navigate('/intelligent-platform/energyUse/application')
                );
              },
            });
          }}
        >
          删除
        </Button>
      ),
    });
  }
  if (detail.admin && status === OutApprovalStatus.DRAFT) {
    actionList.push({
      key: 'edit',
      node: (
        <Button
          color="primary"
          size="large"
          onClick={() => navigate(`/intelligent-platform/energyUse/application/edit/${id}`)}
        >
          编辑
        </Button>
      ),
    });
  }
  if (detail.admin && status === OutApprovalStatus.REJECTED) {
    actionList.push({
      key: 'resubmit',
      node: (
        <Button
          color="primary"
          size="large"
          onClick={() => {
            Dialog.confirm({
              content: '确定重新提交？',
              confirmText: '确定',
              onConfirm: () => {
                resubmitApproval(detail.id!).then(() => navigate('/intelligent-platform/energyUse/application'));
              },
            });
          }}
        >
          重新发起
        </Button>
      ),
    });
  }
  if (detail.approvable) {
    actionList.push({
      key: 'approvalList',
      node: (
        <Button color="primary" fill="none" onClick={() => navigate(`/intelligent-platform/energyUse/application`)}>
          审批列表
        </Button>
      ),
    });
  }

  // 审批列表单独处理，固定在最左边
  const approvalListAction = actionList.find(item => item.key === 'approvalList');
  const otherActions = actionList.filter(item => item.key !== 'approvalList');

  // 其他按钮按优先级排序：优先级高的在右边（数组末尾）
  // 排序后：[低优先级..., 删除, 撤回, 催办, 审批]
  const sortedOtherActions = [...otherActions].sort(
    (a, b) => (ACTION_PRIORITY[b.key] || 99) - (ACTION_PRIORITY[a.key] || 99)
  );
  // 计算其他按钮最多能展示几个
  // 底部总共最多4个按钮：审批列表(占1个) + 更多按钮(占1个) + 其他按钮展示数
  const hasApprovalList = !!approvalListAction;
  const otherMaxVisible = hasApprovalList ? 3 : 4; // 有审批列表时其他按钮最多展示3个

  // 其他按钮超过限制时，需要更多按钮
  const needMoreButton = sortedOtherActions.length > otherMaxVisible;
  const finalOtherMaxVisible = needMoreButton ? otherMaxVisible - 1 : otherMaxVisible; // 有更多按钮时再减1

  const visibleOtherActions = sortedOtherActions.slice(-finalOtherMaxVisible);
  const moreActions = sortedOtherActions.slice(0, sortedOtherActions.length - finalOtherMaxVisible);

  return (
    <div className={styles.page}>
      <div className={styles.detail}>
        <List header="基本信息" className={styles.list}>
          <List.Item>
            <span className={styles.label}>申请部门:</span>
            <span className={styles.value}>{detail?.ownerOrgName}</span>
          </List.Item>
        </List>
        {!isEmpty(detail?.delayEnergyConsumptionDetailList) && (
          <List header="申请明细" mode="card" className={styles.list}>
            {detail?.delayEnergyConsumptionDetailList!.map((item, index) => {
              return (
                <div key={index}>
                  <div className={styles.listTitle}>明细{index + 1}</div>
                  <List.Item>
                    <span className={styles.label}>区域选择:</span>
                    <span className={styles.value}>{item.areaList?.map(i => i.zoneName)?.join('、')}</span>
                  </List.Item>
                  <List.Item>
                    <span className={styles.label}>作业人数:</span>
                    <span className={styles.value}>{item.workers ?? '--'}</span>
                  </List.Item>
                  <List.Item>
                    <span className={styles.label}>作业内容:</span>
                    <span className={styles.value}>{item.content ?? '--'}</span>
                  </List.Item>
                  <List.Item>
                    <span className={styles.label}>用能类型:</span>
                    <span className={styles.value}>{item.mediumList?.map(i => i.mediumName)?.join('、 ') ?? '--'}</span>
                  </List.Item>
                  <List.Item>
                    <span className={styles.label}>开始时间:</span>
                    <span className={styles.value}>
                      {item.startTime ? dayjs(item.startTime).format('YYYY-MM-DD HH:mm') : '--'}
                    </span>
                  </List.Item>
                  <List.Item>
                    <span className={styles.label}>结束时间:</span>
                    <span className={styles.value}>
                      {item.endTime ? dayjs(item.endTime).format('YYYY-MM-DD HH:mm') : '--'}
                    </span>
                  </List.Item>
                  <List.Item>
                    <span className={styles.label}>用能时长:</span>
                    <span className={styles.value}>{item.duration ?? '--'}小时</span>
                  </List.Item>
                </div>
              );
            })}
          </List>
        )}
        <List header="审批流程" mode="card" className={styles.list}>
          <List.Item>
            <Steps direction="vertical">
              {detail.firstApprovalDetail && (
                <Step
                  title="一级审批"
                  status={StepStatusMap[detail.firstApprovalDetail?.approvalResult as ApprovalStatus] as any}
                  description={DescRender(detail.firstApprovalDetail)}
                />
              )}
              {detail.secondApprovalDetail && (
                <Step
                  title="二级审批"
                  status={StepStatusMap[detail.secondApprovalDetail?.approvalResult as ApprovalStatus] as any}
                  description={DescRender(detail.secondApprovalDetail)}
                />
              )}
              {detail.ccStaffIdList && (
                <Step
                  title="抄送人"
                  status={detail.secondApprovalDetail?.approvalResult === ApprovalStatus.APPROVED ? 'finish' : 'wait'}
                  // status="wait"
                  description={
                    <div className={styles.stepDescItem}>
                      <span className={styles.label}>抄送人：</span>
                      <span>{detail.ccStaffIdList.map(i => i.staffName)?.join('、')}</span>
                    </div>
                  }
                />
              )}
            </Steps>
          </List.Item>
        </List>
      </div>
      {actionList.length > 0 && (
        <div className={styles.actionBar}>
          <div className={styles.actionButtons}>
            {/* 审批列表在最左边 */}
            {approvalListAction && <div className={styles.actionButton}>{approvalListAction.node}</div>}
            {/* 更多按钮在第二个位置（row-reverse后） */}
            {moreActions.length > 0 && (
              <div className={styles.actionButton}>
                <Button size="large" onClick={() => setShowMorePopup(true)}>
                  更多
                </Button>
              </div>
            )}
            {/* 从右到左显示：审批、催办、撤回、删除 */}
            {visibleOtherActions.map((action: ActionItem, idx: number) => (
              <div key={idx} className={styles.actionButton}>
                {action.node}
              </div>
            ))}
          </div>
        </div>
      )}
      <Popup
        visible={showMorePopup}
        onMaskClick={() => setShowMorePopup(false)}
        onClose={() => setShowMorePopup(false)}
        position="bottom"
        bodyStyle={{ borderRadius: '8px 8px 0 0' }}
      >
        <div className={styles.morePopup}>
          <div className={styles.morePopupContent}>
            {moreActions.map((action, idx) => (
              <Button
                key={idx}
                fill="none"
                className={styles.morePopupButton}
                onClick={() => {
                  setShowMorePopup(false);
                  // 触发按钮的onClick事件
                  const btn = action.node as React.ReactElement;
                  if (btn.props.onClick) {
                    btn.props.onClick();
                  }
                }}
              >
                {(action.node as React.ReactElement).props.children}
              </Button>
            ))}
          </div>
          <Button onClick={() => setShowMorePopup(false)} className={styles.popupCancelBtn} size="large" fill="none">
            取消
          </Button>
        </div>
      </Popup>
      <Popup
        visible={showApprovalPopup}
        onClose={() => setShowApprovalPopup(false)}
        position="bottom"
        className="actionMenu"
      >
        {showApprovalPopup && <Approval level={level!} onCancel={() => setShowApprovalPopup(false)} />}
      </Popup>
    </div>
  );
};

export default EnergyUseApplicationDetail;
